import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, HardHat, MessageSquare, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatDateTime, formatMoney, formatDate } from "@/lib/format";
import { woStatusTone } from "@/lib/wo";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusChip, statusTone } from "@/components/common/StatusChip";
import { EmptyState } from "@/components/common/EmptyState";
import { VoiceScriptPlayer } from "@/components/common/VoiceScriptPlayer";
import { SubDialog } from "@/features/subs/SubDialog";
import { SubFinanceTab } from "@/features/subs/SubFinanceTab";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const MSG_TONE = { entregado: "green", leido: "green", enviado: "blue", encolado: "gray",
  reintento: "amber", fallido: "red", cancelado: "gray" };
const SEMAFORO_TONE = { rojo: "red", ambar: "amber", verde: "green", gris: "gray" };

const Row = ({ label, value, testId }) => (
  <div className="flex items-start justify-between gap-4 border-b border-line py-2 last:border-0">
    <span className="text-[12px] text-ink-tertiary">{label}</span>
    <span data-testid={testId} className="max-w-[62%] text-right text-[13px] text-ink">
      {value ?? "—"}
    </span>
  </div>
);

const Metric = ({ label, value, testId }) => (
  <div data-testid={testId} className="rounded-[8px] border border-line bg-white px-4 py-3">
    <p className="text-[11.5px] text-ink-tertiary">{label}</p>
    <p className="mt-1 text-[22px] font-semibold leading-none tabular-nums text-ink">{value}</p>
  </div>
);

export default function SubDetailPage() {
  const { id } = useParams();
  const { t } = useT();
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = React.useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["sub", id],
    queryFn: async () => (await api.get(`/subs/${id}`)).data,
  });

  const toggleStatus = useMutation({
    mutationFn: async (status) => (await api.post(`/subs/${id}/status?status=${status}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sub", id] });
      qc.invalidateQueries({ queryKey: ["subs"] });
      toast.success(t("app.saved"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  if (isLoading || !data) {
    return <div className="p-6"><Skeleton className="h-[200px] w-full rounded-[8px]" /></div>;
  }

  const { sub, resumen, ordenes, mensajes, evidencia, finanzas } = data;

  return (
    <>
      <PageHeader
        title={sub.nombre}
        subtitle={`${sub.empresa ? `${sub.empresa} · ` : ""}${sub.telefono}`}
        testId="sub-detail-header"
        breadcrumb={
          <button data-testid="sub-back" onClick={() => navigate("/subcontratistas")}
            className="mb-1 flex items-center gap-1 text-[12px] text-ink-tertiary hover:text-brand">
            <ArrowLeft className="h-3 w-3" /> {t("subs.title")}
          </button>
        }
        actions={
          <>
            <StatusChip tone={sub.canal_preferido === "voz" ? "amber" : "blue"}
              testId="sub-detail-channel" label={t(`channels.${sub.canal_preferido}`)} />
            <StatusChip tone={statusTone(sub.status)} label={t(`status.${sub.status}`)} />
            {sub.pago_semaforo && (
              <StatusChip testId="sub-payment-semaforo" tone={SEMAFORO_TONE[sub.pago_semaforo]}
                label={t(`payment_semaforo.${sub.pago_semaforo}`)} />
            )}
            {can("subs", "update") && (
              <>
                <Button variant="outline" data-testid="sub-edit-button" onClick={() => setEditOpen(true)}
                  className="h-8 rounded-[6px] text-[13px]">
                  <Pencil className="mr-1.5 h-3.5 w-3.5" /> {t("app.edit")}
                </Button>
                <Button variant="outline" data-testid="sub-toggle-status"
                  onClick={() => toggleStatus.mutate(sub.status === "active" ? "inactive" : "active")}
                  className="h-8 rounded-[6px] text-[13px]">
                  {sub.status === "active" ? t("app.deactivate") : t("app.activate")}
                </Button>
              </>
            )}
          </>
        }
      />

      <div className="flex-1 overflow-y-auto p-6" data-testid="sub-detail-page">
        {sub.sabe_leer === false && (
          <p data-testid="sub-detail-no-read" className="mb-4 rounded-[8px] border border-state-warn-soft bg-state-warn-soft px-3 py-2 text-[12.5px] font-medium text-state-warn">
            {t("subs.voice_forced")}
          </p>
        )}

        <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5" data-testid="sub-summary">
          <Metric label={t("subs.metrics.done")} value={resumen.completadas} testId="sub-metric-done" />
          <Metric label={t("subs.metrics.active")} value={resumen.activas} testId="sub-metric-active" />
          <Metric label={t("subs.metrics.avg_hours")} value={`${resumen.tiempo_promedio_horas} h`} testId="sub-metric-hours" />
          <Metric label={t("subs.metrics.cycles")} value={resumen.ciclos_correccion} testId="sub-metric-cycles" />
          {resumen.monto_cents !== undefined ? (
            <Metric label={t("subs.metrics.month")} value={formatMoney(resumen.monto_cents)} testId="sub-metric-month" />
          ) : null}
        </div>

        <Tabs defaultValue="general">
          <TabsList className="mb-4 h-9 rounded-[6px] bg-white">
            <TabsTrigger value="general" data-testid="tab-sub-general" className="text-[13px]">{t("subs.tabs.general")}</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-sub-orders" className="text-[13px]">{t("subs.tabs.orders")} ({ordenes.length})</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-sub-messages" className="text-[13px]">{t("subs.tabs.messages")} ({mensajes.length})</TabsTrigger>
            <TabsTrigger value="evidence" data-testid="tab-sub-evidence" className="text-[13px]">{t("subs.tabs.evidence")}</TabsTrigger>
            <TabsTrigger value="finance" data-testid="tab-sub-finance" className="text-[13px]">{t("finance.title")}</TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <div className="grid gap-4 lg:grid-cols-2">
              <section className="rounded-[8px] border border-line bg-white p-4">
                <h2 className="mb-2 text-[13px] font-semibold text-ink">{t("subs.tabs.general")}</h2>
                <Row label={t("app.name")} value={sub.nombre} testId="sub-detail-name" />
                <Row label={t("subs.company")} value={sub.empresa} />
                <Row label={t("app.phone")} value={sub.telefono} />
                <Row label={t("app.email")} value={sub.email} />
                <Row label={t("subs.specialties")} value={(sub.especialidades_nombres || []).join(", ")} testId="sub-detail-specialties" />
                <Row label={t("subs.channel")} value={t(`channels.${sub.canal_preferido}`)} />
                <Row label={t("subs.can_read")} value={sub.sabe_leer === false ? t("app.no") : t("app.yes")} />
                <Row label={t("subs.settlement")} value={t(`subs.settlements.${sub.modalidad_liquidacion || "quincenal"}`)} />
                <Row label={t("subs.contract_type")} value={t(`subs.contract_types.${sub.tipo_contratacion || "por_contrato"}`)} testId="sub-detail-contract-type" />
                <Row label={t("subs.daily_capacity")} value={sub.capacidad_diaria ?? 3} testId="sub-detail-daily-capacity" />
                {sub.dias_pago_post_trabajo != null && (
                  <Row label={t("subs.pay_days_after")} value={sub.dias_pago_post_trabajo} testId="sub-detail-pay-days-after" />
                )}
                <Row label={t("subs.availability")} value={t(`subs.availabilities.${sub.disponibilidad || "completa"}`)} />
                <Row label={t("subs.rating")} value={sub.calificacion} />
                {sub.tarifa_promedio_cents !== undefined && (
                  <Row label={t("subs.rate")} value={formatMoney(sub.tarifa_promedio_cents)} />
                )}
                <Row label={t("subs.zones")} value={(sub.zonas || []).join(", ")} />
                <Row label={t("app.notes")} value={sub.notas} />
              </section>

              <div className="space-y-4">
                <section className="rounded-[8px] border border-line bg-white p-4">
                  <h2 className="mb-2 text-[13px] font-semibold text-ink">{t("subs.docs")}</h2>
                  {(sub.documentos || []).length ? (
                    (sub.documentos || []).map((d, i) => (
                      <Row key={i} label={`${t(`subs.doc_types.${d.tipo}`) || d.tipo} ${d.numero || ""}`}
                        value={d.vence_at ? `${t("subs.expires")} ${formatDate(d.vence_at)}` : "—"} />
                    ))
                  ) : (
                    <p className="text-[12.5px] text-ink-tertiary">{t("app.empty")}</p>
                  )}
                </section>
                <section data-testid="sub-finance-placeholder"
                  className="flex items-center gap-3 rounded-[8px] border border-dashed border-line-strong bg-surface-sunken p-4">
                  <Wallet className="h-4 w-4 text-ink-tertiary" />
                  <div>
                    <p className="text-[12.5px] font-medium text-ink">{t("subs.finance_title")}</p>
                    <p className="text-[11.5px] text-ink-tertiary">{formatMoney(finanzas.balance.saldo_cents)}</p>
                  </div>
                </section>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <div className="overflow-hidden rounded-[8px] border border-line bg-white">
              {ordenes.length ? (
                <table className="w-full text-[13px]" data-testid="sub-orders-table">
                  <thead className="bg-surface-sunken">
                    <tr className="border-b border-line text-[11.5px] uppercase tracking-[0.04em] text-ink-tertiary">
                      <th className="px-3 py-2 text-left">{t("wo.code")}</th>
                      <th className="px-3 py-2 text-left">{t("wo.property")}</th>
                      <th className="px-3 py-2 text-left">{t("app.status")}</th>
                      <th className="px-3 py-2 text-left">{t("wo.due")}</th>
                      {ordenes.some((o) => o.sub_cost_cents !== undefined) && (
                        <th className="px-3 py-2 text-right">{t("wo.cost")}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {ordenes.map((o) => (
                      <tr key={o.id} data-testid={`sub-order-${o.code}`} onClick={() => navigate(`/ordenes/${o.id}`)}
                        className="h-[44px] cursor-pointer border-b border-line last:border-0 hover:bg-surface-sunken">
                        <td className="px-3 font-medium tabular-nums">{o.code}</td>
                        <td className="px-3">{o.property_nombre} {o.unit_etiqueta ? `· ${o.unit_etiqueta}` : ""}</td>
                        <td className="px-3">
                          <StatusChip tone={woStatusTone(o.status)} label={t(`woStatus.${o.status}`)} />
                        </td>
                        <td className="px-3 text-ink-tertiary">{formatDate(o.due_at)}</td>
                        {o.sub_cost_cents !== undefined && (
                          <td className="px-3 text-right tabular-nums">{formatMoney(o.sub_cost_cents)}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <EmptyState icon={HardHat} testId="sub-orders-empty" title={t("subs.no_orders")} description={t("subs.no_orders_desc")} />
              )}
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <section className="rounded-[8px] border border-line bg-white" data-testid="sub-conversation">
              <header className="border-b border-line px-4 py-2.5">
                <h2 className="text-[13px] font-semibold text-ink">{t("subs.conversation")}</h2>
                <p className="text-[11.5px] text-ink-tertiary">{t("subs.conversation_desc")}</p>
              </header>
              {mensajes.length ? (
                <ul className="space-y-3 bg-surface-sunken p-4">
                  {[...mensajes].reverse().map((m) => m.direccion === "entrante" ? (
                    // Fix O (2026-09-29): lo que contestó el contratista, del lado izquierdo.
                    <li key={m.id} data-testid={`sub-reply-${m.id}`} className="flex justify-start">
                      <div className="max-w-[80%] rounded-[10px] rounded-bl-[2px] border border-line bg-white px-3 py-2">
                        <div className="mb-1 flex flex-wrap items-center gap-1.5">
                          <StatusChip tone="gray" label={t(`channels.${m.canal}`)} />
                          <span className="text-[11px] text-ink-tertiary">{t("subs.reply")}</span>
                        </div>
                        <p className="whitespace-pre-wrap text-[12.5px] leading-snug text-ink">{m.cuerpo}</p>
                        {!!m.adjuntos?.length && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {m.adjuntos.map((a, i) => (
                              <a key={i} href={a.url} target="_blank" rel="noreferrer">
                                <img src={a.thumb_url || a.url} alt=""
                                  className="h-12 w-12 rounded-[4px] border border-line object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                        <p className="mt-1 text-[11px] text-ink-tertiary">
                          {m.wo_code ? `${m.wo_code} · ` : ""}{formatDateTime(m.created_at)}
                        </p>
                      </div>
                    </li>
                  ) : (
                    <li key={m.id} data-testid={`sub-message-${m.id}`} className="flex justify-end">
                      <div className="max-w-[80%] rounded-[10px] rounded-br-[2px] border border-brand bg-white px-3 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                        <div className="mb-1 flex flex-wrap items-center gap-1.5">
                          <StatusChip tone={m.canal === "voz" ? "amber" : "blue"} label={t(`channels.${m.canal}`)} />
                          <StatusChip tone={MSG_TONE[m.estado] || "gray"} label={t(`messages.states.${m.estado}`)} />
                          <span className="text-[11px] text-ink-tertiary">{t(`messages.events.${m.evento}`)}</span>
                        </div>
                        <p className="whitespace-pre-wrap text-[12.5px] leading-snug text-ink">{m.cuerpo}</p>
                        {m.canal === "voz" && (
                          <div className="mt-2">
                            <VoiceScriptPlayer script={m.voice_script || m.cuerpo}
                              label={t("messages.play_script")} testId={`sub-message-play-${m.id}`} />
                          </div>
                        )}
                        {m.respuesta?.tecla && (
                          <p className="mt-1.5 text-[11.5px] font-medium text-state-ok">
                            {t("messages.key_pressed", { k: m.respuesta.tecla })}
                          </p>
                        )}
                        <p className="mt-1 text-right text-[11px] text-ink-tertiary">
                          {m.wo_code ? `${m.wo_code} · ` : ""}{formatDateTime(m.created_at)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState icon={MessageSquare} testId="sub-messages-empty" title={t("subs.no_messages")} description={t("subs.no_messages_desc")} />
              )}
            </section>
          </TabsContent>

          <TabsContent value="evidence">
            {evidencia.length ? (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-6" data-testid="sub-evidence-grid">
                {evidencia.map((e) => (
                  <a key={e.id} href={e.url} target="_blank" rel="noreferrer"
                    className="group overflow-hidden rounded-[8px] border border-line bg-white">
                    <img src={e.thumb_url || e.url} alt="" className="h-[110px] w-full object-cover transition-transform group-hover:scale-[1.03]" />
                    <p className="px-2 py-1.5 text-[11px] text-ink-tertiary">
                      {t(`wo.evidence.types.${e.tipo}`)} · {formatDate(e.created_at)}
                    </p>
                  </a>
                ))}
              </div>
            ) : (
              <EmptyState icon={HardHat} testId="sub-evidence-empty" title={t("subs.no_evidence")} description={t("subs.no_evidence_desc")} />
            )}
          </TabsContent>

          <TabsContent value="finance">
            <SubFinanceTab subId={sub.id} finanzas={finanzas} />
          </TabsContent>
        </Tabs>
      </div>

      <SubDialog open={editOpen} onOpenChange={setEditOpen} sub={sub} />
    </>
  );
}
